import { useNavigate, type NavigateFunction } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useOperationVerificationStatus } from '@/hooks/useOperationVerificationStatus'
import {
  OPERATION_VERIFICATION_FAQ,
  resolveFailureReason,
  toCompletionChangeNotes,
  toFailureImpactNotes,
  toOperationVerificationSummaryViewModel,
} from '@/lib/venue/operationVerificationStatusViewModel'
import type { OperationVerificationView } from '@/lib/venue/operationVerificationTypes'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 운영 인증 현황 — Figma `운영 인증 완료`(14039-39868) · `운영 인증 실패`(14039-39941, `#778`).
 *
 * 조회 전용 화면이다. 신청·재신청은 전부 기존 신청 폼(`operation-verification`)으로 `navigate`한다
 * (설계 §2 자율 결정 2 — surgical, 신청 폼 리팩터링은 이 태스크 범위 밖).
 */
export function VenueOperationVerificationStatusPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const statusQuery = useOperationVerificationStatus(venueId)

  return (
    <>
      <PageHeader title="운영 인증 현황" />

      {statusQuery.isLoading ? <LoadingState rows={4} label="인증 현황을 불러오는 중" /> : null}

      {statusQuery.isError ? (
        <ErrorState
          error={statusQuery.error}
          onRetry={() => {
            void statusQuery.refetch()
          }}
        />
      ) : null}

      {statusQuery.data ? <StatusContent venueId={venueId} view={statusQuery.data} navigate={navigate} /> : null}
    </>
  )
}

function StatusContent({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  switch (view.badge) {
    case 'VERIFIED':
      return <CompletedNotice venueId={venueId} view={view} navigate={navigate} />
    case 'REJECTED':
    case 'EXPIRED':
      return <FailedNotice venueId={venueId} view={view} navigate={navigate} />
    case 'UNDER_REVIEW':
      return <PendingNotice venueId={venueId} navigate={navigate} />
    // 🚨 `NOT_SUBMITTED`와 forward-only 상태(모르는 값)는 같은 화면으로 취급한다 — 아직 보여줄
    // 인증 결과가 없으니 신청을 안내하는 것이 항상 안전한 기본 동작이다.
    default:
      return <NotSubmittedNotice venueId={venueId} navigate={navigate} />
  }
}

function HistoryLink({ venueId, navigate }: { venueId: string; navigate: NavigateFunction }) {
  return (
    <Button
      variant="ghost"
      onClick={() => {
        navigate(`/venues/${venueId}/operation-verification/history`)
      }}
    >
      인증 이력 보기
    </Button>
  )
}

/** Figma `운영 인증 완료`(14039-39868) — 요구사항 4. */
function CompletedNotice({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  const vm = toOperationVerificationSummaryViewModel(view)
  const notes = toCompletionChangeNotes(view)

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="space-y-2 py-10 text-center">
          <Badge tone={vm.tone}>{vm.badgeLabel}</Badge>
          <p className="text-lg font-semibold text-ink-700">운영 인증이 완료되었습니다</p>
          <p className="text-sm text-slateish-600">다음 인증 기한 {vm.verifiedUntilText}</p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="무엇이 달라지나요" />
        <CardBody>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink-700">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="다음 단계" />
        <CardBody className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`/venues/${venueId}/reservation-setting`)
            }}
          >
            예약 기능 활성화
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`/venues/${venueId}/courts`)
            }}
          >
            코트·운영시간 설정
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`/venues/${venueId}/bookings/list`)
            }}
          >
            예약 확인
          </Button>
        </CardBody>
      </Card>

      <div className="flex items-center justify-between">
        <HistoryLink venueId={venueId} navigate={navigate} />
        <Button
          onClick={() => {
            navigate(`/venues/${venueId}/dashboard`)
          }}
        >
          관리 홈으로
        </Button>
      </div>
    </div>
  )
}

/** Figma `운영 인증 실패`(14039-39941) — `REJECTED`·`EXPIRED` 공용, 요구사항 5. */
function FailedNotice({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  const vm = toOperationVerificationSummaryViewModel(view)
  const reason = resolveFailureReason(view)
  const impactNotes = toFailureImpactNotes(view)

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="space-y-3 py-8 text-center">
          <Badge tone={vm.tone}>{vm.badgeLabel}</Badge>
          <p className="text-lg font-semibold text-ink-700">인증이 완료되지 않았어요</p>
        </CardBody>
      </Card>

      <Alert tone="danger" title="사유">
        {reason}
      </Alert>

      <Card>
        <CardHeader title="지금 어떤 영향이 있나요" />
        <CardBody>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink-700">
            {impactNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="자주 묻는 질문" />
        <CardBody className="space-y-4">
          {OPERATION_VERIFICATION_FAQ.map((faq) => (
            <div key={faq.question}>
              <p className="text-sm font-semibold text-ink-700">{faq.question}</p>
              <p className="mt-1 text-sm text-slateish-600">{faq.answer}</p>
            </div>
          ))}
        </CardBody>
      </Card>

      <div className="flex items-center justify-between">
        <HistoryLink venueId={venueId} navigate={navigate} />
        <Button
          onClick={() => {
            navigate(`/venues/${venueId}/operation-verification`)
          }}
        >
          운영 인증 다시 시도
        </Button>
      </div>
    </div>
  )
}

/** `badge=UNDER_REVIEW` — 신청 폼의 진행중 안내와 별개의 경량 버전(설계 §2 자율 결정 7). */
function PendingNotice({ venueId, navigate }: { venueId: string; navigate: NavigateFunction }) {
  return (
    <Card>
      <CardBody className="space-y-2 py-10 text-center">
        <p className="text-base font-semibold text-ink-700">인증 대기 중</p>
        <p className="text-sm text-slateish-600">제출한 신청을 검토하고 있어요. 결과가 나오면 안내해 드려요.</p>
        <div className="flex justify-center gap-2 pt-3">
          <HistoryLink venueId={venueId} navigate={navigate} />
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`/venues/${venueId}/dashboard`)
            }}
          >
            관리 홈으로
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

/** `badge=NOT_SUBMITTED`(요구사항 2 — 인증 필요 배너 + CTA). */
function NotSubmittedNotice({ venueId, navigate }: { venueId: string; navigate: NavigateFunction }) {
  return (
    <div className="space-y-3">
      <Alert
        tone="danger"
        title="운영 인증이 필요합니다"
        action={
          <Button
            onClick={() => {
              navigate(`/venues/${venueId}/operation-verification`)
            }}
          >
            인증 진행하기
          </Button>
        }
      >
        아직 운영 인증을 신청하지 않았어요. 인증을 완료하면 구장이 목록·검색에 노출되고 온라인 예약을 받을 수 있어요.
      </Alert>
    </div>
  )
}
